'use client';

import { createContext, useContext, useState, ReactNode } from 'react';

interface ImportProgress {
  isOpen: boolean;
  isMinimized: boolean;
  totalFiles: number;
  currentFile: string;
  importedCount: number;
  isComplete: boolean;
  isCancelling: boolean;
  error: string;
}

interface ImportContextType {
  importProgress: ImportProgress;
  setImportProgress: (
    progress: ImportProgress | ((prev: ImportProgress) => ImportProgress)
  ) => void;
  minimizeImport: () => void;
  expandImport: () => void;
  closeImport: () => void;
}

const ImportContext = createContext<ImportContextType | undefined>(undefined);

export function ImportProvider({ children }: { children: ReactNode }) {
  const [importProgress, setImportProgress] = useState<ImportProgress>({
    isOpen: false,
    isMinimized: false,
    totalFiles: 0,
    currentFile: '',
    importedCount: 0,
    isComplete: false,
    isCancelling: false,
    error: ''
  });

  const minimizeImport = () => {
    setImportProgress(prev => ({ ...prev, isMinimized: true }));
  };

  const expandImport = () => {
    setImportProgress(prev => ({ ...prev, isMinimized: false }));
  };

  const closeImport = () => {
    setImportProgress(prev => ({ ...prev, isOpen: false, isMinimized: false }));
  };

  return (
    <ImportContext.Provider
      value={{
        importProgress,
        setImportProgress,
        minimizeImport,
        expandImport,
        closeImport
      }}
    >
      {children}
    </ImportContext.Provider>
  );
}

export function useImport() {
  const context = useContext(ImportContext);
  if (context === undefined) {
    throw new Error('useImport must be used within an ImportProvider');
  }
  return context;
}
